/**
 * Admin API for Telegram users management
 */
import { apiClient } from './client';

export interface TelegramUser {
  id: string;
  telegramUserId: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role: 'admin' | 'viewer';
  isActive: boolean;
  status: 'pending' | 'approved' | 'rejected';
  botChatId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdByTelegramUserId?: string | null;
}

export interface CreateTelegramUserDto {
  telegramUserId: string;
  role?: 'admin' | 'viewer';
  isActive?: boolean;
}

export interface UpdateTelegramUserDto {
  role?: 'admin' | 'viewer';
  isActive?: boolean;
  status?: 'pending' | 'approved' | 'rejected';
}

export interface BrandSubscriptionResponse {
  telegramUserId: string;
  brands: string[];
}

export interface AffiliateSubscriptionResponse {
  telegramUserId: string;
  affiliateIds: string[];
}

export interface AvailableAffiliate {
  affiliateId: string;
  brands: string[];
  sources: string[];
}

export const adminUsersApi = {
  /**
   * List all telegram users
   */
  async list(): Promise<TelegramUser[]> {
    return apiClient.get<TelegramUser[]>('/admin/telegram-users');
  },

  /**
   * Create a new telegram user
   */
  async create(data: CreateTelegramUserDto): Promise<TelegramUser> {
    return apiClient.post<TelegramUser>('/admin/telegram-users', data);
  },

  /**
   * Update telegram user
   */
  async update(
    telegramUserId: string,
    data: UpdateTelegramUserDto,
  ): Promise<TelegramUser> {
    return apiClient.patch<TelegramUser>(
      `/admin/telegram-users/${telegramUserId}`,
      data,
    );
  },

  /**
   * Delete telegram user
   */
  async delete(telegramUserId: string): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(
      `/admin/telegram-users/${telegramUserId}`,
    );
  },

  /**
   * Get brand subscriptions for a user
   */
  async getSubscriptions(telegramUserId: string): Promise<BrandSubscriptionResponse> {
    return apiClient.get<BrandSubscriptionResponse>(
      `/admin/telegram-users/${telegramUserId}/subscriptions`,
    );
  },

  /**
   * Set brand subscriptions for a user (replaces all)
   */
  async setSubscriptions(
    telegramUserId: string,
    brands: string[],
  ): Promise<BrandSubscriptionResponse> {
    return apiClient.put<BrandSubscriptionResponse>(
      `/admin/telegram-users/${telegramUserId}/subscriptions`,
      { brands },
    );
  },

  /**
   * Get available affiliate IDs (from events data)
   */
  async getAvailableAffiliates(): Promise<AvailableAffiliate[]> {
    return apiClient.get<AvailableAffiliate[]>('/admin/affiliates');
  },

  /**
   * Get affiliate subscriptions for a user
   */
  async getAffiliateSubscriptions(telegramUserId: string): Promise<AffiliateSubscriptionResponse> {
    return apiClient.get<AffiliateSubscriptionResponse>(
      `/admin/telegram-users/${telegramUserId}/affiliate-subscriptions`,
    );
  },

  /**
   * Set affiliate subscriptions for a user (replaces all)
   */
  async setAffiliateSubscriptions(
    telegramUserId: string,
    affiliateIds: string[],
  ): Promise<AffiliateSubscriptionResponse> {
    return apiClient.put<AffiliateSubscriptionResponse>(
      `/admin/telegram-users/${telegramUserId}/affiliate-subscriptions`,
      { affiliateIds },
    );
  },
};
